/**
 * Doğrudan yükleme dizisi (9.2.4 · D-563).
 *
 * Üç adım: **bilet** al, baytları **doğrudan depoya** yaz, sonra **doğrulat**.
 * Dosya API'den geçmiyor — Cloud Run istek gövdesini 32 MiB'de kesiyor ve
 * 1 GB'lık bir video oradan sığmaz.
 *
 * Adımlar burada, ekranda değil: sıranın kendisi ve hata davranışı sınanabilsin
 * diye. Bağımlılıklar dışarıdan veriliyor, yani test native bir dosyaya ya da
 * gerçek bir kovaya ihtiyaç duymuyor.
 */
import { rejectMedia, type MediaKind, type MediaRejection } from './mediaLimits';

export type MediaTicket = { uploadUrl: string; url: string };

export type MediaUploadDeps = {
  requestTicket: (input: {
    kind: MediaKind;
    contentType: string;
    sizeBytes: number;
    durationSeconds?: number;
  }) => Promise<MediaTicket>;
  /** Baytları imzalı adrese yazar; HTTP durum kodunu döndürür. */
  putFile: (input: { uri: string; uploadUrl: string; contentType: string }) => Promise<number>;
  finalize: (input: { kind: MediaKind; url: string }) => Promise<{ url: string }>;
};

export type MediaUploadInput = {
  kind: MediaKind;
  uri: string;
  contentType: string;
  sizeBytes: number;
  durationSeconds?: number;
};

export type MediaUploadFailure =
  | { step: 'limit'; reason: MediaRejection }
  | { step: 'ticket' }
  | { step: 'put'; status: number }
  | { step: 'finalize' };

export class MediaUploadError extends Error {
  constructor(readonly failure: MediaUploadFailure) {
    super(`Medya yüklenemedi: ${failure.step}`);
  }
}

/**
 * Sıra kesildiğinde geri alınacak bir şey YOK ve bu tasarımın kendisi:
 * doğrulanmamış bilet mesaja dönüşemiyor (sunucudaki `upload_verified_at`
 * şartı), yüklenmiş ama doğrulanmamış nesneyi de 24 saatlik saklama süresi
 * topluyor. Bu yüzden hiçbir adımda temizlik çağrısı yapılmıyor — yapılsaydı
 * ağ koptuğunda çalışmayacak bir temizliğe güvenmiş olurduk.
 */
export async function uploadMedia(
  deps: MediaUploadDeps,
  input: MediaUploadInput
): Promise<{ url: string }> {
  const red = rejectMedia(input.kind, input.sizeBytes, input.durationSeconds);
  if (red) throw new MediaUploadError({ step: 'limit', reason: red });

  let ticket: MediaTicket;
  try {
    ticket = await deps.requestTicket({
      kind: input.kind,
      contentType: input.contentType,
      sizeBytes: input.sizeBytes,
      durationSeconds: input.durationSeconds,
    });
  } catch {
    throw new MediaUploadError({ step: 'ticket' });
  }

  let status: number;
  try {
    status = await deps.putFile({
      uri: input.uri,
      uploadUrl: ticket.uploadUrl,
      contentType: input.contentType,
    });
  } catch {
    throw new MediaUploadError({ step: 'put', status: 0 });
  }
  if (status < 200 || status >= 300) {
    // Yükleme başarısızsa DOĞRULAMA HİÇ ÇAĞRILMIYOR: yarım yüklenmiş bir
    // nesneyi damgalatmak, mesaja bozuk bir video bağlamak olurdu.
    throw new MediaUploadError({ step: 'put', status });
  }

  try {
    await deps.finalize({ kind: input.kind, url: ticket.url });
  } catch {
    throw new MediaUploadError({ step: 'finalize' });
  }
  return { url: ticket.url };
}
